import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

// First path segments of content + knowledge-hub pages (used when the admin
// scopes the note to "content pages only").
const CONTENT_HUB = new Set([
  "kathas", "mantras", "suktas", "stotras", "rituals", "festivals", "granthas",
  "scriptures", "vedic-knowledge", "glossary",
  "philosophy", "learn", "rishis", "samskaras", "sanskrit", "pilgrimage",
  "deities", "temples", "jyotirlingas", "shakti-peethas", "mahavidya", "hub", "panchang", "calendar",
]);

// A compact, unobtrusive note shown at the top of pages on every load / refresh.
// Not dismissible. Scope ("all" | "content") is admin-controlled.
export function ContentDisclaimer() {
  const { content_disclaimer, routeSlugs } = useSettings();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const d = content_disclaimer || {};
  const body = (d.body || "").trim();
  if (d.enabled === false || !body) return null;

  // Auto-hide after the admin-set "show until" date (IST calendar day, inclusive).
  const until = (d.show_until || "").trim();
  if (until) {
    const today = new Date(Date.now() + (new Date().getTimezoneOffset() + 330) * 60000)
      .toISOString()
      .slice(0, 10);
    if (today > until) return null;
  }

  if ((d.scope || "all") === "content") {
    const seg = (pathname || "").replace(/^\//, "").split("/")[0];
    const canonical = (Object.entries(routeSlugs || {}).find(([, v]) => v === seg) || [])[0] || seg;
    if (!CONTENT_HUB.has(canonical)) return null;
  }

  const long = body.length > 170;
  const preview = long ? `${body.slice(0, 170).trimEnd()}…` : body;

  return (
    <div className="mx-auto max-w-7xl px-5 pt-4 md:px-8" data-testid="content-disclaimer">
      <div className="border-l-2 border-[var(--gold)]/60 bg-[var(--surface-alt)]/40 px-4 py-2.5">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-ink-3">
            {d.title && <span className="mr-1.5 font-medium uppercase tracking-wide text-gold">{d.title}:</span>}
            <span className="whitespace-pre-line">{open ? body : preview}</span>
          </p>
          {long && (
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid="content-disclaimer-toggle"
              className="shrink-0 whitespace-nowrap text-xs text-gold underline-offset-2 hover:underline">
              {open ? "less" : "more"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentDisclaimer;
